import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { PDPSelectedRootProductConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedRootProductConditionalBlock/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";
import { isoProductID } from "tsi-common-react/src/models/nominals";
import { Template as ChangeRootProductBlockTemplate } from "tsi-common-react/src/templates/ChangeRootProductBlockTemplate";

import { templates } from "@reactivated";
import type {
    ChangeRootOptionBlock as ChangeRootOptionBlockValue,
    FullReviewsBlock as FullReviewsBlockValue,
    CocoonPromoConditionalBlock as PromoConditionalBlockValue,
    SelectedRootProductConditionalBlock as RootConditionalBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildProductPageBaseComponentsMap } from "../../blocks/componentMaps";
import { CocoonFullReviewsBlock } from "../FullReviewsBlock";

import styles from "./index.module.scss";

const renderChangeRootTitleHTML = (title: string): string => {
    const [firstWord = "", ...restWords] = title.split(" ");
    const rest = restWords.join(" ");
    return `<span> ${firstWord} </span>${rest ? ` ${rest}` : ""}`;
};

const CocoonChangeRootOptionBlock = (
    props: Immutable<{
        value: ChangeRootOptionBlockValue;
        ssr: templates.ChangeRootProductBlockTemplate | null;
    }>,
): React.JSX.Element => {
    const title = props.value.title ?? "";

    return (
        <div
            tabIndex={0}
            id="pdp-change-root-block"
            className={classNames(
                "pdp-change-root-option-block",
                "l-capped-width",
                props.value.optionInputType,
            )}
        >
            {title ? (
                <h2
                    className={styles.changeRootOptionTitle}
                    dangerouslySetInnerHTML={{
                        __html: renderChangeRootTitleHTML(title),
                    }}
                ></h2>
            ) : null}
            {props.ssr ? (
                <ChangeRootProductBlockTemplate {...props.ssr} />
            ) : null}
        </div>
    );
};

type ProductDisplayBodyValue = NonNullable<
    templates.ProductDisplayPageTemplate["page"]["body"]
>["value"];
type ProductLineBodyValue = NonNullable<
    templates.ProductLinePageTemplate["page"]["body"]
>["value"];

type IProps = Immutable<{
    value: ProductDisplayBodyValue | ProductLineBodyValue;
    reviews: {
        [k: string]: templates.ProductReviewsTemplate;
    };
    changeRootSSR: {
        [k: string]: templates.ChangeRootProductBlockTemplate;
    };
    rootConditionalSSR: {
        [k: string]: templates.SelectedRootProductConditionalBlockTemplate;
    };
    pageSlug?: string | null;
}>;

const ProductPageBody = (props: IProps): React.JSX.Element => {
    const productPageBaseComponentsMap = buildProductPageBaseComponentsMap({
        pageSlug: props.pageSlug,
        isProductPageBody: true,
    });
    const components = {
        ...productPageBaseComponentsMap,
        full_reviews_block: (block: {
            id?: string;
            value: Immutable<FullReviewsBlockValue>;
        }) => (
            <CocoonFullReviewsBlock
                value={block.value}
                reviews={(block.id ? props.reviews[block.id] : null) ?? null}
            />
        ),
        change_root_option_block: (block: {
            id?: string;
            value: Immutable<ChangeRootOptionBlockValue>;
        }) => (
            <CocoonChangeRootOptionBlock
                value={block.value}
                ssr={(block.id ? props.changeRootSSR[block.id] : null) ?? null}
            />
        ),
        promo_conditional_block: (block: {
            value: Immutable<PromoConditionalBlockValue>;
        }) => (
            <PromoConditionalBlock value={block.value}>
                <StreamField
                    tagName={React.Fragment}
                    value={block.value.content ?? []}
                    components={components}
                />
            </PromoConditionalBlock>
        ),
        root_product_conditional_block: (block: {
            id?: string;
            value: Immutable<RootConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.rootConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedRootProductConditionalBlock
                    blockProductID={isoProductID.wrap(ssr.block_product_id)}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedRootProductConditionalBlock>
            );
        },
    };

    return (
        <StreamField
            tagName={React.Fragment}
            value={props.value}
            components={components}
        />
    );
};

export const ProductDisplayPageBody = ProductPageBody;
export const ProductLinePageBody = ProductPageBody;
